import { useCallback } from "react";
import _ from "lodash";

import { CheckIcon, CloseIcon, MoreHorizIcon } from "icons";
import { colors } from "theme/palette";

import ButtonDropdown from "components/ButtonDropdown";
import { MenuItem } from "components/Menu";
import Stack from "components/Stack";
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "components/Table";
import Text from "components/Text";
import toast from "components/Toast";
import Tooltip from "components/Tooltip";
import { useConfirmationDialog } from "components/useConfirmationDialog";
import UserAvatar from "components/UserAvatar";

import {
  addUserToAllCompanyDeals,
  IEmployee,
  removeUserFromAllCompanyDeals,
  updateUserIsActive,
  updateUserIsAdmin,
} from "entities/Employee/sdk";
import { creationDateRetrieveReadable } from "entities/Notification/utils";
import { IUser } from "entities/User/sdk";

interface Employee
  extends Pick<
    IEmployee,
    | "id"
    | "avatar"
    | "is_admin"
    | "is_active"
    | "first_name"
    | "last_name"
    | "job_title"
    | "email"
    | "contact"
    | "last_login"
  > {}

export interface ICompanyTeamTable {
  employees: Employee[];
  onUpdated?: (employee: Employee) => void;
  user: IUser;
  active: boolean;
  tableDataTestid?: string;
}

const CompanyTeamTable = ({
  employees,
  user,
  onUpdated,
  active,
  tableDataTestid,
}: ICompanyTeamTable) => {
  const { show: showConfirmationDialog } = useConfirmationDialog();

  const updateEmployee = useCallback(
    (employee: Employee, isAdmin: boolean) =>
      updateUserIsAdmin(employee.id, isAdmin).then((updatedEmployee) => {
        onUpdated && onUpdated(updatedEmployee);
      }),
    [onUpdated]
  );

  const deactivateUser = useCallback(
    ({ employee }: { employee: Employee }) =>
      showConfirmationDialog({
        onConfirm: () =>
          updateUserIsActive({ userId: employee.id, isActive: false }).then(
            (updatedEmployee) => {
              if (onUpdated) {
                onUpdated(updatedEmployee);
              }
            }
          ),
        confirmButtonText: "Deactivate",
        message: "Are you sure you want to deactivate this user?",
      }),
    [onUpdated, showConfirmationDialog]
  );

  const reactivateUser = useCallback(
    ({ employee }: { employee: Employee }) =>
      showConfirmationDialog({
        onConfirm: () =>
          updateUserIsActive({ userId: employee.id, isActive: true }).then(
            (updatedEmployee) => {
              if (onUpdated) {
                onUpdated(updatedEmployee);
              }
            }
          ),
        confirmButtonText: "Reactivate",
        message: "Are you sure you want to reactivate this user?",
      }),
    [onUpdated, showConfirmationDialog]
  );

  const addToAllCompanyDeals = useCallback(
    ({ userId, asAdmin }: { userId: number; asAdmin: boolean }) => {
      addUserToAllCompanyDeals({ userId, asAdmin }).then(() => {
        toast.successMessage(
          `Added to all company deals${asAdmin ? " as admin" : ""}!`
        );
      });
    },
    []
  );

  const removeFromAllCompanyDeals = useCallback(
    ({ userId }: { userId: number }) => {
      removeUserFromAllCompanyDeals({ userId }).then(() => {
        toast.successMessage("Removed from all company deals!");
      });
    },
    []
  );

  return (
    <Table data-testid={tableDataTestid}>
      <TableHead>
        <TableRow
          sx={{
            ".MuiTableCell-root": {
              borderBottom: `1px solid ${colors.blue20}`,
            },
          }}
        >
          <TableCell>
            <Text variant="labelSmall" sx={{ color: colors.gray40 }}>
              Id
            </Text>
          </TableCell>
          <TableCell>
            <Text variant="labelSmall" sx={{ color: colors.gray40 }} noWrap>
              Name &amp; Job title
            </Text>
          </TableCell>
          <TableCell>
            <Text variant="labelSmall" sx={{ color: colors.gray40 }}>
              Email &amp; Phone No.
            </Text>
          </TableCell>
          <TableCell>
            <Text variant="labelSmall" sx={{ color: colors.gray40 }}>
              Last login
            </Text>
          </TableCell>
          <TableCell>
            <Text variant="labelSmall" sx={{ color: colors.gray40 }}>
              Admin rights
            </Text>
          </TableCell>
          {user?.is_admin && <TableCell></TableCell>}
        </TableRow>
      </TableHead>
      <TableBody data-testid="company-team-members-table">
        {employees.map((employee) => (
          <TableRow
            key={employee.id}
            sx={{
              ".MuiTableCell-root": {
                borderBottom: `1px solid ${colors.blue20}`,
              },
            }}
            data-testid={`company-team-data-row-for-user-id-${employee.id}`}
            data-test-id="company-team-member-data-row"
          >
            <TableCell>
              <UserAvatar
                src={employee.avatar ? employee.avatar : undefined}
                imgix
                imgixWidth={40}
                imgixHeight={40}
                variant="rounded"
                sx={{ width: 40, height: 40 }}
                dataTestid={
                  employee.avatar
                    ? "custom-team-member-avatar"
                    : "default-team-member-avatar"
                }
              />
            </TableCell>
            <TableCell>
              <Stack alignItems="flex-start">
                <Tooltip title={`${employee.first_name} ${employee.last_name}`}>
                  <Text
                    variant="text2"
                    data-testid="company-member-full-name"
                    noWrap
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                    }}
                  >
                    {employee.first_name} {employee.last_name}
                  </Text>
                </Tooltip>
                <Tooltip title={employee.job_title}>
                  <Text
                    variant="labelSmall"
                    data-testid="company-member-job"
                    noWrap
                    sx={{
                      color: colors.gray40,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: 200,
                    }}
                  >
                    {employee.job_title}
                  </Text>
                </Tooltip>
              </Stack>
            </TableCell>
            <TableCell>
              <Tooltip title={employee.email}>
                <Text
                  variant="text2"
                  data-testid="company-member-email"
                  noWrap
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: 150,
                  }}
                >
                  {employee.email}
                </Text>
              </Tooltip>
              <Text
                variant="labelSmall"
                sx={{ color: colors.gray40 }}
                data-testid="company-member-phone"
              >
                {employee.contact}
              </Text>
            </TableCell>
            <TableCell>
              <Text variant="labelSmall" sx={{ color: colors.gray40 }} noWrap>
                {_.isNil(employee.last_login)
                  ? "Never"
                  : creationDateRetrieveReadable({
                      creationDate: employee.last_login,
                    })}
              </Text>
            </TableCell>
            <TableCell>
              <Text
                variant="labelSmall"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
                data-testid={
                  employee.is_admin ? "admin-user-icon" : "regular-user-icon"
                }
              >
                {employee.is_admin ? <CheckIcon /> : <CloseIcon />}
              </Text>
            </TableCell>
            {user?.is_admin && (
              <TableCell>
                <ButtonDropdown
                  icon={<MoreHorizIcon />}
                  label=""
                  variant="text"
                  width="auto"
                  buttonDataTestid="user-actions-button"
                >
                  {active ? (
                    <span>
                      {employee.is_admin && (
                        <MenuItem
                          onClick={() => updateEmployee(employee, false)}
                        >
                          <Text variant="text2">Revoke as Admin</Text>
                        </MenuItem>
                      )}
                      {!employee.is_admin && (
                        <MenuItem
                          onClick={() => updateEmployee(employee, true)}
                        >
                          <Text variant="text2">Add as Admin</Text>
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() =>
                          addToAllCompanyDeals({
                            userId: employee.id,
                            asAdmin: false,
                          })
                        }
                      >
                        <Text variant="text2">Add to all company deals</Text>
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          addToAllCompanyDeals({
                            userId: employee.id,
                            asAdmin: true,
                          })
                        }
                      >
                        <Text variant="text2">
                          Add to all company deals as admin
                        </Text>
                      </MenuItem>
                      <MenuItem
                        onClick={() =>
                          removeFromAllCompanyDeals({ userId: employee.id })
                        }
                      >
                        <Text variant="text2">
                          Remove from all company deals
                        </Text>
                      </MenuItem>
                      {user.id !== employee.id && (
                        <MenuItem
                          onClick={() =>
                            deactivateUser({
                              employee,
                            })
                          }
                        >
                          <Text variant="text2">Deactivate user</Text>
                        </MenuItem>
                      )}
                    </span>
                  ) : (
                    <MenuItem onClick={() => reactivateUser({ employee })}>
                      <Text variant="text2">Reactivate user</Text>
                    </MenuItem>
                  )}
                </ButtonDropdown>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default CompanyTeamTable;
